import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileBarChart, Mail, Copy, Download, Trash2, Loader2 } from "lucide-react";
import { api, errMsg, fmtDT, useFetch } from "../lib/api";
import { StatusBadge } from "../components/common/kit";
import { Md } from "./Ai";

function ReportView({ r, onDelete }) {
  const [sending, setSending] = useState(false);
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([r.content], { type: "text/markdown" }));
    a.download = `${r.title}.md`; a.click(); URL.revokeObjectURL(a.href);
  };
  const email = async () => {
    setSending(true);
    try { const res = await api.post(`/ai/reports/${r.id}/email`); toast.success(`Report emailed to ${res.data.to}`); } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };
  return <div className="card-c p-5" data-testid="ai-report-view">
    <div className="flex flex-wrap items-start gap-3 mb-3"><div className="flex-1"><p className="h3" data-testid="ai-report-title">{r.title}</p><p className="text-xs text-neutral-500">{fmtDT(r.created_at)} · {r.model}</p></div>
      {r.status === "ready" && <div className="flex flex-wrap gap-2">
        <button className="btn-outline-o" onClick={() => { navigator.clipboard.writeText(r.content); toast.success("Copied"); }} data-testid="ai-report-copy"><Copy className="h-4 w-4" />Copy</button>
        <button className="btn-outline-o" onClick={download} data-testid="ai-report-download"><Download className="h-4 w-4" />Download</button>
        <button className="btn-grad" disabled={sending} onClick={email} data-testid="ai-report-email"><Mail className="h-4 w-4" />{sending ? "Sending..." : "Email me"}</button></div>}
      <button className="btn-ghost-o" onClick={() => onDelete(r.id)} data-testid="ai-report-delete"><Trash2 className="h-4 w-4" /></button></div>
    {r.status === "generating" && <p className="flex items-center gap-2 text-sm text-neutral-500 py-10 justify-center" data-testid="ai-report-generating"><Loader2 className="h-4 w-4 animate-spin" />Writing your report from the latest records — this takes up to a minute...</p>}
    {r.status === "failed" && <p className="text-sm text-red-700 bg-red-50 rounded-xl p-3" data-testid="ai-report-error">{r.error}</p>}
    {r.status === "ready" && <><div className="mb-2"><StatusBadge value="AI draft" /></div><div className="text-sm" data-testid="ai-report-content"><Md text={r.content} /></div></>}
  </div>;
}

export function AiReports({ model }) {
  const { data, reload } = useFetch("/ai/reports");
  const [sel, setSel] = useState(null);
  const reports = data?.reports || [];
  const cur = reports.find((x) => x.id === sel) || reports[0];
  const generating = reports.some((x) => x.status === "generating");
  useEffect(() => {
    if (!generating) return undefined;
    const t = setInterval(reload, 4000);
    return () => { clearInterval(t); };
  }, [generating, reload]);
  const make = async (kind) => {
    try { const r = await api.post("/ai/reports", { kind, model }); setSel(r.data.id); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this report?")) return;
    try { await api.delete(`/ai/reports/${id}`); setSel(null); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return <div className="grid lg:grid-cols-[260px_1fr] gap-5 [&>*]:min-w-0" data-testid="ai-reports">
    <div className="space-y-4">
      <div className="card-c p-3"><p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 px-1 mb-2">One-click reports</p>
        {(data?.kinds || []).map((k) => <button key={k.key} disabled={generating} onClick={() => make(k.key)} className="w-full flex items-center gap-2 text-left text-sm px-3 py-2 rounded-lg hover:bg-orange-50 hover:text-[#FF5A00] disabled:opacity-50 transition-colors" data-testid={`ai-report-make-${k.key}`}><FileBarChart className="h-4 w-4 text-[#FF5A00]" />{k.title}</button>)}</div>
      {reports.length > 0 && <div className="card-c p-3"><p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 px-1 mb-2">Saved reports</p>
        {reports.map((x) => <button key={x.id} onClick={() => setSel(x.id)} className={`w-full text-left text-sm px-3 py-2 rounded-lg truncate ${cur?.id === x.id ? "bg-orange-50 text-[#FF5A00]" : "hover:bg-neutral-50"}`} data-testid={`ai-report-item-${x.id}`}>{x.title}</button>)}</div>}
    </div>
    {cur ? <ReportView r={cur} onDelete={del} /> : <div className="card-c p-10 text-center text-sm text-neutral-500" data-testid="ai-reports-empty">Pick a report on the left. It's written from the records you're allowed to see, and you can save, download or email it.</div>}
  </div>;
}
